import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight, ArrowUpRight, Star } from "lucide-react";
import { MaskLines } from "./Motion";
import { SITE, IMG, EASE } from "@/lib/site";
import { useGoTo } from "@/hooks/useGoTo";

const Seal = () => (
  <div className="relative h-32 w-32" data-testid="hero-seal">
    <svg viewBox="0 0 120 120" className="spin-slow absolute inset-0 h-full w-full">
      <defs><path id="circ" d="M60,60 m-48,0 a48,48 0 1,1 96,0 a48,48 0 1,1 -96,0" /></defs>
      <text className="fill-paper font-mono" style={{ fontSize: 9.5, letterSpacing: 3.2 }}>
        <textPath href="#circ">GARANTIE DÉCENNALE • ÉCOLABEL • ARTISAN ANGEVIN •</textPath>
      </text>
    </svg>
    <div className="absolute inset-[30%] flex items-center justify-center rounded-full bg-gold text-ink font-display text-2xl italic">10<span className="text-xs not-italic ml-0.5">ans</span></div>
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const goTo = useGoTo();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-25%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [3, -3]), { stiffness: 50, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-4, 4]), { stiffness: 50, damping: 18 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      ref={ref}
      onMouseMove={onMove}
      data-testid="hero-section"
      className="grain relative min-h-[100svh] overflow-hidden bg-ink text-paper"
      style={{ perspective: 1400 }}
    >
      <motion.div className="absolute inset-0" style={{ y: imgY, rotateX: rx, rotateY: ry, scale: 1.06 }}>
        <motion.div
          className="absolute inset-0"
          initial={{ clipPath: "inset(24% 34% 24% 34%)" }}
          animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
          transition={{ duration: 1.8, ease: EASE, delay: 0.1 }}
        >
          <motion.img
            src={IMG.hero}
            alt="Salon repeint en bleu nuit par Style Peinture Décoration à Angers"
            className="h-full w-full object-cover"
            initial={{ scale: 1.4 }}
            animate={{ scale: 1 }}
            transition={{ duration: 2.4, ease: EASE, delay: 0.1 }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-ink/95 via-ink/60 to-ink/10" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/40" />
        </motion.div>
      </motion.div>

      <motion.div style={{ y: textY, opacity: fade }} className="relative z-10 mx-auto flex min-h-[100svh] max-w-[1400px] flex-col justify-end px-5 pb-10 pt-32 sm:px-8 lg:pb-14">
        <motion.p
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1, ease: EASE, delay: 1.1 }}
          className="eyebrow mb-7 flex items-center gap-4 text-gold"
          data-testid="hero-eyebrow"
        >
          <span className="h-px w-10 bg-gold" /> Artisan peintre · {SITE.city} & {SITE.region}
        </motion.p>
        <h1 className="font-display text-[2.9rem] leading-[0.98] sm:text-7xl lg:text-[6.6rem] font-normal tracking-[-0.02em] max-w-5xl" data-testid="hero-title">
          <MaskLines
            onLoad
            delay={0.9}
            lines={[
              "Artisan Peintre",
              "Professionnel",
              <>à <em className="text-gold">{SITE.city}</em> et ses alentours</>,
            ]}
          />
        </h1>
        <div className="mt-10 grid gap-10 lg:grid-cols-12 lg:items-end">
          <motion.div
            className="lg:col-span-6"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: EASE, delay: 1.5 }}
          >
            <p className="max-w-md text-base sm:text-lg text-paper/85 leading-relaxed">
              Peinture intérieure, ravalement de façade, revêtements murs & sols. Des finitions d'atelier, un chantier propre, un devis gratuit sous 48h.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/devis" data-testid="hero-devis-button" className="group inline-flex items-center gap-3 bg-gold px-7 py-4 font-semibold text-ink transition-colors duration-300 hover:bg-paper">
                Demander un devis gratuit <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:rotate-45" />
              </Link>
              <button onClick={() => goTo("realisations")} data-testid="hero-realisations-button" className="group inline-flex items-center gap-3 border border-paper/35 px-7 py-4 font-medium transition-colors duration-300 hover:border-paper hover:bg-paper/10">
                Voir nos réalisations <ArrowDownRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-y-1" />
              </button>
            </div>
          </motion.div>
          <motion.div
            className="lg:col-span-6 flex items-end justify-between gap-6 border-t border-paper/20 pt-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 1.8 }}
          >
            <dl className="grid grid-cols-3 gap-5 sm:gap-10">
              {[[SITE.founded, "atelier fondé"], ["48h", "devis gratuit"], ["5,0", "note Google"]].map(([n, l]) => (
                <div key={l}>
                  <dt className="font-display text-3xl sm:text-5xl">{n}</dt>
                  <dd className="mt-1 text-xs text-paper/75 flex items-center gap-1">
                    {l === "note Google" && <Star className="h-3 w-3 fill-gold text-gold" />}{l}
                  </dd>
                </div>
              ))}
            </dl>
            <div className="hidden md:block shrink-0"><Seal /></div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};
